import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Box, Button, Slider, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';

// Helper to get the cropped image as a data URL
function getCroppedImg(imageSrc, croppedAreaPixels) {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = Math.min(croppedAreaPixels.width, croppedAreaPixels.height);
      canvas.width = size;
      canvas.height = size;
      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        size,
        size,
        0,
        0,
        size,
        size
      );
      ctx.restore();
      resolve(canvas.toDataURL('image/png'));
    };
  });
}



export default function CircularImageCropper({ element, updateElement, ElementControls, isInZone = false, layoutId = null, zoneId = null, onElementClick = null }) {
  const [imageSrc, setImageSrc] = useState(element?.imageSrc || '');
  const [crop, setCrop] = useState(element?.crop || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(element?.zoom || 1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(element?.croppedAreaPixels || null);
  const [croppedImage, setCroppedImage] = useState(element?.croppedImage || '');
  const [editing, setEditing] = useState(!element?.croppedImage);
  
  // Resize functionality - using the same approach as normal images
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStartData, setResizeStartData] = useState(null);
  const isResizingRef = useRef(false);
  const resizeStartDataRef = useRef(null);
  const resizeDirectionRef = useRef(null);
  const containerRef = useRef(null);

  const currentSize = element?.styles?.width || 200;

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (imageSrc && croppedAreaPixels) {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setEditing(false);
      
      // Update the element with the cropped image data and size
      updateElement(element.id, {
        imageSrc,
        croppedImage: cropped,
        crop,
        zoom,
        croppedAreaPixels,
        styles: {
          ...element.styles,
          width: currentSize,
          height: currentSize
        }
      }, isInZone, layoutId, zoneId);
    }
  }, [imageSrc, croppedAreaPixels, crop, zoom, element?.id, updateElement, isInZone, layoutId, zoneId, currentSize, element?.styles]);

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (imageSrc && croppedAreaPixels && editing) {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setEditing(false);
      
      // Update the element with the cropped image data and size
      updateElement(element.id, {
        imageSrc,
        croppedImage: cropped,
        crop,
        zoom,
        croppedAreaPixels,
        styles: {
          ...element.styles,
          width: currentSize,
          height: currentSize
        }
      }, isInZone, layoutId, zoneId);
    }
  }, [imageSrc, croppedAreaPixels, crop, zoom, element?.id, updateElement, isInZone, layoutId, zoneId, currentSize, element?.styles, editing]);

  const handleEdit = () => setEditing(true);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const newImageSrc = ev.target.result;
        setImageSrc(newImageSrc);
        setEditing(true);
        setCroppedImage('');
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        
        // Update the element with the new image source
        updateElement(element.id, {
          imageSrc: newImageSrc,
          croppedImage: '',
          crop: { x: 0, y: 0 },
          zoom: 1,
          croppedAreaPixels: null
        }, isInZone, layoutId, zoneId);
      };
      reader.readAsDataURL(file);
    }
  };



  // Resize handlers - using the same approach as normal images
  const handleResizeMove = useCallback((e) => {
    if (!isResizingRef.current || !resizeStartDataRef.current) {
      console.log('🔵 CircularImage resize move skipped:', { 
        isResizing: isResizingRef.current, 
        hasData: !!resizeStartDataRef.current 
      });
      return;
    }
    
    e.preventDefault();
    const deltaX = e.clientX - resizeStartDataRef.current.startX;
    const deltaY = e.clientY - resizeStartDataRef.current.startY;
    
    let newSize = resizeStartDataRef.current.startSize;
    
    // Calculate new size based on resize direction
    switch (resizeDirectionRef.current) {
      case 'se':
        newSize = Math.max(50, Math.min(500, resizeStartDataRef.current.startSize + Math.max(deltaX, deltaY)));
        break;
      case 'sw':
        newSize = Math.max(50, Math.min(500, resizeStartDataRef.current.startSize + Math.max(-deltaX, deltaY)));
        break;
      case 'ne':
        newSize = Math.max(50, Math.min(500, resizeStartDataRef.current.startSize + Math.max(deltaX, -deltaY)));
        break;
      case 'nw':
        newSize = Math.max(50, Math.min(500, resizeStartDataRef.current.startSize + Math.max(-deltaX, -deltaY)));
        break;
      default:
        newSize = Math.max(50, Math.min(500, resizeStartDataRef.current.startSize + Math.max(Math.abs(deltaX), Math.abs(deltaY))));
        break;
    }
    
    console.log('🔵 CircularImage resize move:', { 
      direction: resizeDirectionRef.current, 
      deltaX, 
      deltaY, 
      oldSize: resizeStartDataRef.current.startSize, 
      newSize 
    });
    
    // Update element immediately for smooth resize
    updateElement(element.id, {
      styles: {
        ...element.styles,
        width: newSize,
        height: newSize
      }
    }, isInZone, layoutId, zoneId);
  }, [element, updateElement, isInZone, layoutId, zoneId]);

  const handleResizeEnd = useCallback(async () => {
    setIsResizing(false);
    setResizeDirection(null);
    setResizeStartData(null);
    isResizingRef.current = false;
    resizeDirectionRef.current = null;
    resizeStartDataRef.current = null;
    
    // Auto-save if editing
    if (editing && imageSrc && croppedAreaPixels) {
      await autoSave();
    }
    
    // Remove global event listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove, editing, imageSrc, croppedAreaPixels, autoSave]);

  const handleResizeStart = useCallback((e, direction) => {
    console.log('🔵 CircularImage resize start:', { direction, currentSize, elementId: element.id });
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeDirection(direction);
    isResizingRef.current = true;
    resizeDirectionRef.current = direction;
    
    const startX = e.clientX;
    const startY = e.clientY;
    
    // Parse currentSize to ensure it's a number
    let startSize = currentSize;
    if (typeof startSize === 'string') {
      startSize = parseInt(startSize.replace('px', ''));
    }
    if (isNaN(startSize)) {
      startSize = 200; // fallback to default
    }
    
    const resizeData = {
      startX,
      startY,
      startSize,
      elementId: element.id
    };
    
    console.log('🔵 CircularImage resize data:', resizeData);
    setResizeStartData(resizeData);
    resizeStartDataRef.current = resizeData;
    
    // Add global event listeners
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [currentSize, element.id, handleResizeMove, handleResizeEnd]);

  return (
    <Box 
      sx={{
        width: currentSize,
        display: 'inline-block',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        cursor: 'pointer',
        verticalAlign: 'top'
      }}
      onClick={async (e) => {
        e.stopPropagation();
        
        // Auto-save if we're currently editing
        if (editing && imageSrc && croppedAreaPixels) {
          await autoSave();
        }
        
        if (onElementClick) {
          onElementClick(element, isInZone, layoutId, zoneId);
        }
      }}
    >
      <ElementControls />

      <Box 
        ref={containerRef}
        sx={{
          width: currentSize,
          height: currentSize,
          borderRadius: '50%',
          backgroundColor: '#e0e0e0',
          position: 'relative',
          overflow: 'visible',
          mb: 0, // Remove margin for inline layouts
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isResizing ? 'nw-resize' : 'default'
        }}
      >
        {croppedImage && !editing ? (
          <>
            <img
              src={croppedImage}
              alt="Cropped"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleEdit}
              sx={{ 
                position: 'absolute', 
                bottom: -60, 
                left: '50%', 
                transform: 'translateX(-50%)',
                zIndex: 2
              }}
            >
              Crop
            </Button>
          </>
        ) : imageSrc ? (
          <>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </>
        ) : (
          <label style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <span style={{ color: '#888', fontSize: '12px' }}>Drag or click to upload</span>
          </label>
        )}

        {/* Resize handles */}
        {imageSrc && (
          <>
            {/* Top-left handle */}
            <Box
              sx={{
                position: 'absolute',
                top: -6,
                left: -6,
                width: 16,
                height: 16,
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                cursor: 'nw-resize',
                zIndex: 20,
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }
              }}
              onMouseDown={(e) => handleResizeStart(e, 'nw')}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Top-right handle */}
            <Box
              sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 16,
                height: 16,
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                cursor: 'ne-resize',
                zIndex: '20',
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }
              }}
              onMouseDown={(e) => handleResizeStart(e, 'ne')}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Bottom-left handle */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -6,
                left: -6,
                width: 16,
                height: 16,
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                cursor: 'sw-resize',
                zIndex: 20,
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }
              }}
              onMouseDown={(e) => handleResizeStart(e, 'sw')}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Bottom-right handle */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -6,
                right: -6,
                width: 16,
                height: 16,
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                cursor: 'se-resize',
                zIndex: 20,
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }
              }}
              onMouseDown={(e) => handleResizeStart(e, 'se')}
              onClick={(e) => e.stopPropagation()}
            />
          </>
        )}
      </Box>
      
      {imageSrc && editing && (
        <Box sx={{ width: 180, mt: 1 }}>
          <Slider
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(_, value) => setZoom(value)}
            aria-labelledby="Zoom"
          />
        </Box>
      )}
    </Box>
  );
}